/**
 * Evidence —— **憑證總覽**（會計師／管理層）
 * 一次過睇所有交易嘅憑證（相／PDF），連憑單摘要；可篩（公司／月份／搜尋）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api, fmtMoney, type EvidenceItem, type Meta } from '../lib/api';

export default function Evidence({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entityId, setEntityId] = useState('');
  const [period, setPeriod] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<EvidenceItem[]>([]);
  const [summary, setSummary] = useState({ files: 0, vouchers: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setBusy(true); setErr(null);
    api.evidence({ entityId: entityId || undefined, period: period || undefined, q: q || undefined, limit: 200 })
      .then((r) => { setItems(r.items); setSummary(r.summary); })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  }, [entityId, period, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <main className="page">
      <TopBar title={t('evidence.heading')} />

      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('expense.entity')}</span>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              <option value="">{t('list.all')}</option>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('list.month')}</span>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('evidence.search')}</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('evidence.search_hint')} />
          </label>
        </div>
        <p className="note">{t('evidence.count', { files: summary.files, vouchers: summary.vouchers })}</p>
        {busy && <p className="note">{t('list.loading')}</p>}
        {err && <p className="err">{err}</p>}
      </section>

      {items.length === 0 && !busy && <p className="note">{t('evidence.empty')}</p>}

      <div className="gallery">
        {items.map((it) => (
          <button key={it.id} type="button" className="ev-card" onClick={() => navigate(`/vouchers/${it.voucher_id}`)}>
            <span className="ev-thumb">
              {it.mime === 'application/pdf'
                ? <span className="ev-pdf">📄 PDF</span>
                : <img src={`/api/attachments/${it.id}`} alt="" loading="lazy" />}
            </span>
            <span className="ev-meta">
              <b>{it.voucher_no}</b>
              <small>{it.txn_date}</small>
              <small className="ev-purpose">{it.purpose}</small>
              <small className={it.voucher_kind === 'receipt' ? 'pos' : 'neg'}>{fmtMoney(it.amount_cents)}</small>
              {it.vendor_name && <small>{it.vendor_name}</small>}
              {it.customer_name && <small>{it.customer_name}</small>}
            </span>
          </button>
        ))}
      </div>
    </main>
  );
}
